import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { Col, ModalBody, Row, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import uuid from 'react-uuid';

import { LabelInput, LabelRequiredInput, LabelError } from 'shared/components/Labels';
import { TextField } from 'shared/components/form/Inputs';
import { Button } from 'shared/components/Buttons';
import MainSpinner from 'shared/components/MainSpinner';

import {
  closeContactForm,
  refreshContacts,
  setAddContacts,
  updateContact,
} from 'redux/actions/contactActions';
import { showAlert } from 'redux/actions/alertActions';
import useApi from 'hooks/useApi';
import useLanguages from 'hooks/useLanguages';

import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS, cleanSpaceString } from 'shared/utils';
import { colors } from 'shared/defaultTheme';
import CustomSelect from 'shared/components/CustomSelect';
import { Checkbox } from 'shared/components/form/CheckBox';

import { ReactComponent as UnlockIcon } from 'assets/icon-unlock.svg';
import { ReactComponent as LockIcon } from 'assets/icon-lock.svg';
import { ReactComponent as FullInfoIcon } from 'assets/icon-info-full.svg';
import { ReactComponent as RefreshIcon } from 'assets/icon-refresh.svg';
import { ReactComponent as InformationIcon } from 'assets/icon-information.svg';

import { ContactModalFormWrapper, CheckboxClientLink } from '../styles';

const WITHOUT_SYNC = 0;
const AUTO_SYNC = 1;
const MANUALLY_SYNC = 2;

const ContactModalForm = ({
  clientId,
  updating,
  newTypeOrganization,
  newClientName,
  newContactPhone,
  languageField,
}) => {
  const dispatch = useDispatch();
  const { openContactModalForm, editContact, addContacts } = useSelector((state) => state.contact);
  const { currentClient } = useSelector((state) => state.clientState);
  const { cusNavId } = useSelector((state) => state.auth);
  const theme = useSelector((state) => state.theme);
  const { syncContacts, hasClientLink } = theme;
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const { t } = useTranslation();
  const { languages } = useLanguages();
  const newIsAccess = useRef(null);
  const newSyncClientlink = useRef(null);
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });
  const { customAreaApi } = useApi();
  newIsAccess.current = watch('isAccess', false);
  newSyncClientlink.current = watch('syncClientlink', false);
  const [disableButton, setDisabledButton] = useState(false);
  const [, getFilterContactByEmail] = customAreaApi(
    {
      url: `${window.API_V1}/cli/contacts/${cusNavId}/search/all`,
      method: 'POST',
    },
    { manual: true }
  );

  useEffect(() => {
    if (newTypeOrganization === '1' && !clientId && addContacts.length === 0) {
      const fullName = newClientName.split(' ');
      setValue('name', fullName[0]);
      setValue('surname', fullName[1]);
      setValue('surnameTwo', fullName[2]);
      setValue('phone', newContactPhone);
    }
    if (!openContactModalForm) {
      setDisabledButton(false);
    }
    if (
      openContactModalForm &&
      !editContact &&
      hasClientLink &&
      (syncContacts === MANUALLY_SYNC || syncContacts === AUTO_SYNC)
    ) {
      setValue('syncClientlink', true);
    }
  }, [newTypeOrganization, openContactModalForm]);

  useEffect(() => {
    if (editContact) {
      const keyField = 'id';
      Object.keys(editContact).forEach((field) => {
        if (field !== keyField) {
          setValue(field, editContact[field]);
          setValue('isAccess', editContact.hasAccess);
          if (editContact?.contactState === 'sended') {
            setValue('isAccess', true);
          }
          if (editContact?.contactState === 'access') {
            setValue('isAccess', true);
          }
        }
      });
    }
  }, [editContact]);
  const getTitle = () => {
    if (editContact) {
      return t('client_contact_update');
    }
    return t('client_contact_add_new');
  };
  const getButtonText = () => {
    if (editContact) {
      return t('general_button_update');
    }
    return t('general_add');
  };
  const [{ loading }, postContact] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );
  const onCancel = () => {
    setValue('name', '');
    setValue('surname', '');
    setValue('surnameTwo', '');
    setValue('phone', '');
    setValue('mail', '');
    setValue('description', '');
    setValue('isAccess', false);
    setValue('syncClientlink', false);
    reset({
      name: '',
      surname: '',
      surnameTwo: '',
      phone: '',
      mail: '',
      description: '',
      isAccess: false,
      syncClientlink: false,
    });
    dispatch(closeContactForm());
  };

  const validateMailFromDB = async ({ mail }) => {
    try {
      const {
        data: { data },
      } = await getFilterContactByEmail({
        data: {
          filters: [
            {
              model: 'ClientAccess',
              field: 'ClientId',
              filter: [currentClient?.clientId],
            },
            {
              model: 'Contact',
              field: 'Mail',
              filter: [mail],
            },
          ],
          reduce: {
            limit: 0,
            skip: 0,
          },
        },
      });
      if (data && data.length > 0) {
        return false;
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  const validateMailFromState = (values) => {
    const emailExist = addContacts.some((x) => x.mail === values.mail);
    if (!emailExist) {
      dispatch(
        setAddContacts([
          ...addContacts,
          { ...values, id: uuid(), domain: window.location.origin, isBD: false },
        ])
      );
      onCancel();
    } else {
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('client_contact_email_exist'),
        })
      );
    }
  };

  const getMessage = (response, values) => {
    const existErrorSmtp = response.data.errors.some((x) => x.code === 'CCNO-18');
    if (response.data.errors[0].code === 'CCCO-12') {
      return t('client_management_contact_error_synchronize');
    }
    if (response.data.errors[0].code === 'CCCO-09') {
      return t('client_contact_error_email_exist', {
        value: values.mail,
      });
    }
    if (existErrorSmtp) {
      onCancel();
      return t('client_management_contact_error_smtp');
    }
    return t('general_error_message');
  };

  const onSubmit = async (values) => {
    if (!updating) {
      if (editContact) {
        const editContacts = addContacts.map((contact) => {
          if (contact.mail === editContact?.mail) {
            return { ...contact, ...values };
          }
          return { ...contact };
        });
        onCancel();
        dispatch(setAddContacts(editContacts));
      } else {
        validateMailFromState(values);
      }
    } else {
      try {
        setDisabledButton(true);

        if (!editContact) {
          const isValidMail = await validateMailFromDB({ mail: values.mail });
          if (!isValidMail) {
            dispatch(
              showAlert({
                alertType: ALERT_TYPE_ERROR,
                alertMessage: t('client_contact_email_exist'),
              })
            );
            setDisabledButton(false);
            return;
          }
        }

        const url = editContact
          ? `${window.API_V1}/cli/contacts/${cusNavId}/${clientId}/modify`
          : `${window.API_V1}/cli/contacts/${cusNavId}`;
        const data = editContact
          ? { ...values, id: editContact.clientId, domain: window.location.origin }
          : [
              {
                ...values,
                id: uuid(),
                clients: [{ clientId }],
              },
            ];
        const {
          data: { data: dataResponse, errors: postErrors },
        } = await postContact({
          url,
          data,
        });
        if (postErrors.length) {
          throw new Error('Error throwing by endpoint');
        }
        if (editContact) {
          dispatch(updateContact(data));
          dispatch(refreshContacts(true));
        } else {
          dispatch(refreshContacts(true));
        }
        if (!dataResponse) {
          dispatch(
            showAlert({
              alertType: ALERT_TYPE_ERROR,
              alertMessage: t('general_error_message'),
            })
          );
          setDisabledButton(false);
          return;
        }
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_SUCCESS,
            alertMessage: t('general_success_message'),
          })
        );
        onCancel();
        setDisabledButton(false);
      } catch (ex) {
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_ERROR,
            alertMessage: getMessage(ex.response, values),
          })
        );
        setDisabledButton(false);
      }
    }
  };

  const showAccessIcon = (contactState) => {
    if (contactState === 'sended') {
      return (
        <>
          <LockIcon id="sended" fill={colors.indochine} height={18} width={18} />
          <UncontrolledTooltip placement="top" target="sended">
            {t('client_management_access_send')}
          </UncontrolledTooltip>
        </>
      );
    }
    if (contactState === 'access') {
      return (
        <>
          <UnlockIcon id="access" fill={colors.bilbao} height={18} width={18} />
          <UncontrolledTooltip placement="top" target="access">
            {t('client_management_access_active')}
          </UncontrolledTooltip>
        </>
      );
    }
    return (
      <>
        <LockIcon id="no_has_access" fill={colors.error} height={18} width={18} />
        <UncontrolledTooltip placement="top" target="no_has_access">
          {t('client_management_no_has_access')}
        </UncontrolledTooltip>
      </>
    );
  };
  return (
    <ContactModalFormWrapper isOpen={openContactModalForm} size="lg">
      <div className="title-wrapper">
        <h1 className="title">{getTitle()}</h1>
      </div>
      <ModalBody>
        <form className="form" noValidate onSubmit={handleSubmit(onSubmit)}>
          <Row className="w-100 mb-3">
            <Col className="col-left" sm={6}>
              <div className="form__form-group">
                <LabelRequiredInput
                  className="form__form-group-label"
                  label={t('general_name')}
                  color={colors.black}
                  error={errors.name !== undefined}
                />
                <Controller
                  name="name"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: { value: true, message: t('general_required_field') },
                    maxLength: {
                      value: 180,
                      message: t('general_max_length_field').replace('{param}', 180),
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      value={field.value}
                      onChange={(e) => {
                        const newEvent = cleanSpaceString(e);
                        field.onChange(newEvent);
                      }}
                      onBlur={field.onBlur}
                      error={errors.name !== undefined}
                    />
                  )}
                />
                {errors.name && <LabelError label={errors.name.message} />}
              </div>
            </Col>
            <Col sm={6}>
              <div className="form__form-group">
                <LabelInput
                  className="form__form-group-label"
                  label={t('general_sur_name1')}
                  color={colors.black}
                />
                <Controller
                  name="surname"
                  control={control}
                  defaultValue=""
                  rules={{
                    maxLength: {
                      value: 100,
                      message: t('general_max_length_field').replace('{param}', 100),
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      value={field.value}
                      onChange={(e) => {
                        const newEvent = cleanSpaceString(e);
                        field.onChange(newEvent);
                      }}
                      onBlur={field.onBlur}
                      error={errors.surname !== undefined}
                    />
                  )}
                />
                {errors.surname && <LabelError label={errors.surname.message} />}
              </div>
            </Col>
          </Row>
          <Row className="w-100 mb-3">
            <Col className="col-left" sm={6}>
              <div className="form__form-group">
                <LabelInput
                  className="form__form-group-label"
                  label={t('general_sur_name2')}
                  color={colors.black}
                />
                <Controller
                  name="surnameTwo"
                  control={control}
                  defaultValue=""
                  rules={{
                    maxLength: {
                      value: 100,
                      message: t('general_max_length_field').replace('{param}', 100),
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={errors.surnameTwo !== undefined}
                    />
                  )}
                />
                {errors.surnameTwo && <LabelError label={errors.surnameTwo.message} />}
              </div>
            </Col>
            <Col sm={6}>
              <div className="form__form-group">
                <LabelInput
                  className="form__form-group-label"
                  label={t('general_phone')}
                  color={colors.black}
                />
                <Controller
                  name="phone"
                  control={control}
                  defaultValue=""
                  rules={{
                    maxLength: {
                      value: 80,
                      message: t('general_max_length_field').replace('{param}', 80),
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      value={field.value}
                      error={errors.phone !== undefined}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  )}
                />
                {errors.phone && <LabelError label={errors.phone.message} />}
              </div>
            </Col>
          </Row>
          <Row className="w-100 mb-2">
            <Col className="col-left" sm={6}>
              <div className="form__form-group">
                <LabelRequiredInput
                  className="form__form-group-label"
                  label={t('general_email')}
                  color={colors.black}
                  error={errors.mail !== undefined}
                />
                <Controller
                  name="mail"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: { value: true, message: t('general_required_field') },
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('general_invalid_email'),
                    },
                    maxLength: {
                      value: 80,
                      message: t('general_max_length_field').replace('{param}', 80),
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={errors.mail !== undefined}
                      isReadOnly={!!editContact}
                      withButtonClear={!editContact}
                    />
                  )}
                />
                {errors.mail && <LabelError label={errors.mail.message} />}
              </div>
            </Col>
            <Col sm={6}>
              <div className="form__form-group">
                <LabelInput
                  className="form__form-group-label"
                  label={t('general_description')}
                  color={colors.black}
                />
                <Controller
                  name="description"
                  control={control}
                  defaultValue=""
                  rules={{
                    maxLength: {
                      value: 300,
                      message: t('general_max_length_field').replace('{param}', 300),
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={errors.description !== undefined}
                    />
                  )}
                />
                {errors.description && <LabelError label={errors.description.message} />}
              </div>
            </Col>
          </Row>
          <Row className="w-100">
            <Col sm={6} className="col-left d-flex">
              <div className="form__form-group">
                <LabelInput
                  className="form__form-group-label"
                  label={t('general_language')}
                  color={colors.black}
                  error={errors.clientName != null}
                />
                <Controller
                  name="i18"
                  control={control}
                  defaultValue={
                    currentClient?.clientI18 || languageField || selectedDefaultLanguage
                  }
                  rules={{ required: { value: true, message: t('general_required_field') } }}
                  render={({ field }) => (
                    <CustomSelect
                      options={languages}
                      defaultValue={field.value}
                      backgroundColor={colors.transparent}
                      onChange={field.onChange}
                      className="w-100 mw-100 contact-custom-select"
                    />
                  )}
                />
              </div>
            </Col>
            <Col className="col-left d-flex" sm={6}>
              <div>
                <div className="d-flex align-items-end">
                  {(syncContacts === MANUALLY_SYNC ||
                    (syncContacts === WITHOUT_SYNC && editContact?.syncClientlink)) && (
                    <div className="form__form-group sync-client-link">
                      <div className="sync-client-link-action">
                        <RefreshIcon
                          fill={newSyncClientlink.current ? colors.black : colors.suvaGrey}
                          width={18}
                          height={18}
                        />
                        <LabelInput
                          className="ml-1"
                          label={t('general_customizer_label_sync_client')}
                          color={newSyncClientlink.current ? colors.black : colors.suvaGrey}
                        />
                      </div>
                      <Controller
                        name="syncClientlink"
                        control={control}
                        defaultValue
                        rules={{
                          required: { value: false, message: t('general_required_field') },
                        }}
                        render={({ field }) => (
                          <CheckboxClientLink isDisabled={editContact?.syncClientlink}>
                            <Checkbox
                              checked={field.value}
                              onClick={field.onChange}
                              isDisabled={editContact?.syncClientlink}
                            />
                          </CheckboxClientLink>
                        )}
                      />
                      {errors.syncClientlink && (
                        <LabelError label={errors.syncClientlink.message} />
                      )}
                    </div>
                  )}
                  <div className="form__form-group give-access-container">
                    <div className="give-access-icon-label">
                      {showAccessIcon(editContact?.contactState)}
                      <LabelInput
                        className="ml-1"
                        label={t('client_management_give_access')}
                        color={newIsAccess.current ? colors.black : colors.suvaGrey}
                      />
                      <FullInfoIcon
                        fill={colors.black}
                        width={13}
                        height={13}
                        className="ml-1"
                        id="fullInfoIcon"
                      />
                      <UncontrolledTooltip
                        placement="top"
                        target="fullInfoIcon"
                        className="full-info-icon-tooltip"
                      >
                        {t('client_access_info_text')}
                      </UncontrolledTooltip>
                    </div>
                    <Controller
                      name="isAccess"
                      control={control}
                      defaultValue={false}
                      rules={{
                        required: { value: false, message: t('general_required_field') },
                      }}
                      render={({ field }) => (
                        <Checkbox checked={field.value} onClick={field.onChange} />
                      )}
                    />
                    {errors.isAccess && <LabelError label={errors.isAccess.message} />}
                  </div>
                </div>
                <div className="w-100">
                  {hasClientLink ? (
                    <>
                      {(syncContacts === MANUALLY_SYNC ||
                        (syncContacts === WITHOUT_SYNC && editContact?.syncClientlink)) && (
                        <div
                          className="client-link-info"
                          aria-hidden="true"
                          onClick={() => {
                            window.open(
                              `https://ecosistema.lefebvre.es/client-link-simulador/`,
                              '_blank'
                            );
                          }}
                        >
                          <InformationIcon className="what-is-client-link-icon mr-1" />
                          <span className="what-is-client-link-span">
                            {t('general_config_what_is_client_link')}
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="no-have-contract-client-link-container">
                      {/* <div className="d-flex align-items-center">
                        <InformationIcon className="no-have-contract-client-link-information-icon mr-1 mb-1" />
                        <span className="no-have-contract-client-link-span">
                          {t('general_no_have_contract_clint_link')}
                        </span>
                      </div>
                      <div className="ml-2">
                        <div
                          className="no-have-contract-client-link-btn"
                          aria-hidden="true"
                          onClick={() => {
                            window.open(
                              `https://ecosistema.lefebvre.es/client-link-simulador/`,
                              '_blank'
                            );
                          }}
                        >
                          {t('general_request_info')}
                        </div>
                      </div> */}
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
          <Row className="w-100 mt-4">
            <Col sm={12} className="d-flex flex-row justify-content-end">
              <Button
                className="mr-3 text-uppercase"
                type="button"
                color="secondary"
                label={t('general_button_cancel')}
                onClick={onCancel}
              />
              <Button
                type="submit"
                color="primary"
                label={getButtonText()}
                disabled={disableButton}
                className="text-uppercase"
              />
            </Col>
          </Row>
        </form>
        {loading && <MainSpinner />}
      </ModalBody>
    </ContactModalFormWrapper>
  );
};

ContactModalForm.defaultProps = {
  clientId: undefined,
  newTypeOrganization: '',
  newClientName: '',
  newContactPhone: '',
  languageField: '',
};

ContactModalForm.propTypes = {
  clientId: PropTypes.string,
  updating: PropTypes.bool.isRequired,
  newTypeOrganization: PropTypes.string,
  newClientName: PropTypes.string,
  newContactPhone: PropTypes.string,
  languageField: PropTypes.string,
};

export default ContactModalForm;
